// Office "Lone worker safety" page (Session AOB). Who is on site right now, who has gone quiet, and
// the one control the office has when somebody has.
//
// WHAT THIS ANSWERS THAT NOTHING DID. An engineer working alone in a motor room or a lift shaft had
// nothing on this platform that would notice if something went wrong and they stopped responding.
// Now every visit is opened by an explicit arrival tap and closed by an explicit departure tap, and
// the platform watches the clock in between.
//
// THE OVERDUE LIST COMES FIRST AND IS THE POINT. A session is overdue the moment its deadline
// passes, which is THIRTY MINUTES BEFORE the office is alerted about it: this page shows the problem
// while a phone call can still resolve it, rather than only once the alarm has gone off. Everything
// else on the page is context for that list.
//
// THERE IS NO CHECK-IN BUTTON HERE, AND THERE MUST NEVER BE ONE. A check-in is an explicit tap by the
// person on site (Director decision). A control on this page that checked an engineer in would let
// somebody at a desk satisfy a requirement about somebody in a shaft, which is the entire property
// the feature provides. What the office can do is CLOSE a session: state that it has looked into
// this one and it is dealt with, on the record and with a note.
import { useCallback, useEffect, useState } from "react";
import { HardHat, PhoneCall } from "lucide-react";
import { ApiError, api } from "../../lib/api";
import type { LoneWorkerSessionDto, LoneWorkerSessions } from "../../lib/api";
import { ModalShell, MODAL_ACTION_BTN, MODAL_ACTIONS } from "../../components/ModalShell";
import { ConfirmDeleteModal } from "../../components/ConfirmDeleteModal";
import { presentableServerMessage } from "../../lib/serverMessage";
import { useAuth } from "../../lib/auth";
import { Button } from "../../components/Button";
import { Loader } from "../../components/Loader";
import { PageHeader } from "../../components/PageHeader";
import { StatusPill } from "../../components/StatusPill";
import { formatTime } from "../../lib/format";

const fieldLabelCls = "mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body";
const fieldInputCls =
  "w-full rounded-ele border border-ele-purple-accent bg-ele-field-inset px-3 py-1.5 text-sm text-ele-strong outline-none focus:border-ele-green";

function errText(err: unknown, fallback: string): string {
  return (err instanceof ApiError ? presentableServerMessage(err.message) : null) ?? fallback;
}

/**
 * A stored instant as the London wall-clock time an office user would say, "20:05". The whole
 * platform renders instants as London time; a raw UTC render is an hour out all summer.
 */
export function timeOfDay(iso: string): string {
  return formatTime(iso);
}

/** "1 hour 5 minutes", "45 minutes". Mirrors the server's own formatOverdue so the page and the alert read alike. */
export function formatMinutes(mins: number): string {
  if (mins < 60) return `${mins} minute${mins === 1 ? "" : "s"}`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  const hours = `${h} hour${h === 1 ? "" : "s"}`;
  return m === 0 ? hours : `${hours} ${m} minute${m === 1 ? "" : "s"}`;
}

/**
 * What the office is told about one overdue session, in a sentence. Pure, so the three states are
 * testable without a render.
 *
 * The distinction that matters is between OVERDUE (the deadline has passed, nothing has been sent
 * yet) and ALERTED (the escalation has started). Collapsing them would hide the half-hour in which
 * the office can still fix this quietly.
 */
export function overdueSummary(s: LoneWorkerSessionDto, firstAlertAfterMins: number): string {
  const over = formatMinutes(s.overdueMins ?? 0);
  if (!s.alerted) {
    const left = Math.max(0, firstAlertAfterMins - (s.overdueMins ?? 0));
    return `Check-in was due at ${timeOfDay(s.nextCheckInDueAt ?? s.lastCheckInAt)}, ${over} ago. Call them now: the alert goes out in ${formatMinutes(left)}.`;
  }
  return `Check-in was due at ${timeOfDay(s.nextCheckInDueAt ?? s.lastCheckInAt)}, ${over} ago. The office has been alerted ${s.alertCount === 1 ? "once" : `${s.alertCount} times`}.`;
}

/**
 * The next-of-kin fallback for an overdue row, or null when the engineer has not supplied one --
 * which is the common case and must render as nothing at all, never a placeholder. Pure, so the
 * partial-data cases (a name with no phone, a phone with no name) are testable without a render:
 * only the pieces actually on file are shown.
 */
export interface NextOfKinInfo {
  /** "Priya Sharma, wife" / "Priya Sharma" / "wife" -- whatever of name+relationship is on file. */
  label: string | null;
  phone: string | null;
}

export function nextOfKinInfo(s: LoneWorkerSessionDto): NextOfKinInfo | null {
  const name = s.engineerNextOfKinName;
  const relationship = s.engineerNextOfKinRelationship;
  const phone = s.engineerNextOfKinPhone;
  if (!name && !relationship && !phone) return null;
  return { label: [name, relationship].filter(Boolean).join(", ") || null, phone: phone || null };
}

/** The quiet line under an on-site engineer who is not overdue. Pure. */
export function onSiteSummary(s: LoneWorkerSessionDto): string {
  const since = `On site since ${timeOfDay(s.startedAt)}`;
  if (!s.nextCheckInDueAt) return since;
  return `${since}. Next check-in due by ${timeOfDay(s.nextCheckInDueAt)}.`;
}

interface CloseEditor {
  session: LoneWorkerSessionDto;
  note: string;
}

export function LoneWorkerPage() {
  const { user } = useAuth();
  const [data, setData] = useState<LoneWorkerSessions | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [closeEditor, setCloseEditor] = useState<CloseEditor | null>(null);
  const [closeError, setCloseError] = useState<string | null>(null);
  const [closing, setClosing] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<LoneWorkerSessionDto | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);

  // `background` for the 60-second poll below (Session ASB review, F4): a screen left open must not keep the office
  // session alive for ever, so the timer's request does not count as activity (the page still refreshes).
  const load = useCallback(async (background = false) => {
    try {
      setData(await api.loneWorker.sessions(background ? { background: true } : {}));
      setState("ready");
    } catch {
      setState("error");
    }
  }, []);

  useEffect(() => {
    setState("loading");
    void load();
  }, [load]);

  // A LIVE PAGE, refreshed on a timer. Everything on it is a clock: an overdue figure that was right
  // when the page loaded is wrong a minute later, and this is the one office screen somebody might
  // sit and watch while ringing an engineer who is not answering. Sixty seconds, which is well inside
  // the 15-minute escalation and nowhere near often enough to matter to the server.
  useEffect(() => {
    const t = setInterval(() => void load(true), 60_000);
    return () => clearInterval(t);
  }, [load]);

  async function closeSession() {
    if (!closeEditor) return;
    setClosing(true);
    setCloseError(null);
    try {
      await api.loneWorker.close(closeEditor.session.id, closeEditor.note);
      setCloseEditor(null);
      await load();
    } catch (err) {
      setCloseError(errText(err, "Could not close this check-in. Please try again."));
    } finally {
      setClosing(false);
    }
  }

  async function removeSession() {
    if (!confirmRemove) return;
    setRemoving(true);
    setRemoveError(null);
    try {
      await api.loneWorker.remove(confirmRemove.id);
      setConfirmRemove(null);
      await load();
    } catch (err) {
      setRemoveError(errText(err, "Could not delete this check-in. Please try again."));
    } finally {
      setRemoving(false);
    }
  }

  const isAdmin = user?.role === "admin";

  return (
    <div>
      <PageHeader
        className="mb-4"
        title="Lone worker safety"
        subtitle="Engineers check in when they arrive on site and check out when they leave. If a visit runs outside normal working hours they check in again about every hour while they are still there. If a check-in does not arrive, you are told."
      />

      {state === "loading" ? (
        <Loader label="Loading the lone worker check-ins..." />
      ) : state === "error" ? (
        <p role="alert" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center text-ele-strong">
          Could not load the lone worker check-ins. Please try again in a moment.
        </p>
      ) : !data ? null : (
        <>
          {/* OVERDUE. Red, first, and unmissable: this is the only part of the page anybody opens it
              in a hurry for. */}
          {data.overdue.length > 0 && (
            <section aria-labelledby="lw-overdue" className="mb-6 rounded-ele border border-ele-danger bg-ele-surface p-4">
              <h2 id="lw-overdue" className="font-heading text-lg text-ele-danger">
                {data.overdue.length === 1 ? "One engineer has missed a check-in" : `${data.overdue.length} engineers have missed a check-in`}
              </h2>
              <ul className="mt-3 space-y-3">
                {data.overdue.map((s) => {
                  const nok = nextOfKinInfo(s);
                  return (
                  <li key={s.id} className="rounded-ele border border-ele-purple-accent bg-ele-inset p-3">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-heading text-lg tracking-wide text-ele-strong">{s.engineerName}</p>
                        <p className="text-sm text-ele-body">
                          {s.siteName} · {s.ref}
                        </p>
                        <p className="mt-1 text-sm text-ele-strong">{overdueSummary(s, data.policy.firstAlertAfterMins)}</p>
                        {/* Next of kin: the office's fallback if the call above goes unanswered. Quiet
                            and secondary on purpose -- most rows will not have one, which is normal,
                            and this must never read as an equal alternative to calling the engineer. */}
                        {nok && (
                          <p className="mt-1 text-xs text-ele-body opacity-70">
                            Next of kin, if you cannot reach them{nok.label ? `: ${nok.label}` : ""}
                            {nok.phone && (
                              <>
                                {nok.label ? " · " : ": "}
                                <a href={`tel:${nok.phone.replace(/\s+/g, "")}`} className="text-ele-body underline decoration-dotted">
                                  {nok.phone}
                                </a>
                              </>
                            )}
                          </p>
                        )}
                      </div>
                      <div className="flex min-w-0 flex-wrap gap-2">
                        {s.engineerMobile && (
                          <a
                            href={`tel:${s.engineerMobile.replace(/\s+/g, "")}`}
                            className="inline-flex items-center gap-1.5 rounded-ele bg-ele-green px-4 py-2 font-heading text-sm tracking-wide text-ele-bg transition hover:brightness-110 max-sm:min-h-11"
                          >
                            <PhoneCall aria-hidden size={15} />
                            Call {s.engineerMobile}
                          </a>
                        )}
                        <button
                          onClick={() => {
                            setCloseError(null);
                            setCloseEditor({ session: s, note: "" });
                          }}
                          className="rounded-ele border border-ele-purple-accent px-4 py-2 text-sm text-ele-body transition hover:border-ele-green hover:text-ele-strong max-sm:min-h-11"
                        >
                          They are safe: close this
                        </button>
                      </div>
                    </div>
                  </li>
                  );
                })}
              </ul>
            </section>
          )}

          {/* ON SITE NOW. The ordinary state, and worth seeing on its own: at 9pm it answers "who is
              still out" without anybody having to be overdue first. */}
          <section aria-labelledby="lw-onsite" className="mb-6 rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
            <h2 id="lw-onsite" className="flex items-center gap-2 font-heading text-lg text-ele-blue">
              <HardHat aria-hidden size={18} />
              On site now
            </h2>
            {data.onSite.length === 0 ? (
              <p className="mt-2 text-sm text-ele-body">
                {data.overdue.length > 0
                  ? "Nobody else is checked in on site."
                  : "Nobody is checked in on site at the moment."}
              </p>
            ) : (
              <ul className="mt-3 space-y-2">
                {data.onSite.map((s) => (
                  <li key={s.id} className="flex flex-wrap items-start justify-between gap-3 rounded-ele border border-ele-purple-accent bg-ele-inset p-3">
                    <div className="min-w-0">
                      <p className="font-heading text-base tracking-wide text-ele-strong">{s.engineerName}</p>
                      <p className="text-sm text-ele-body">
                        {s.siteName} · {s.ref}
                      </p>
                      <p className="mt-1 text-xs text-ele-body">{onSiteSummary(s)}</p>
                    </div>
                    <button
                      onClick={() => {
                        setCloseError(null);
                        setCloseEditor({ session: s, note: "" });
                      }}
                      className="shrink-0 rounded-ele border border-ele-purple-accent px-3 py-1.5 text-xs text-ele-body transition hover:border-ele-green hover:text-ele-strong max-sm:min-h-11"
                    >
                      Close this check-in
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section aria-labelledby="lw-recent" className="overflow-hidden rounded-ele border border-ele-purple-accent">
            <h2 id="lw-recent" className="bg-ele-inset px-3 py-2 font-menu text-xs uppercase tracking-widest text-ele-blue">
              Finished in the last two weeks
            </h2>
            {data.recent.length === 0 ? (
              <p className="p-4 text-sm text-ele-body">No check-ins have been recorded yet.</p>
            ) : (
              <table className="ele-list-table w-full text-left text-sm">
                <colgroup>
                  <col style={{ width: "22%" }} />
                  <col style={{ width: "30%" }} />
                  <col style={{ width: "20%" }} />
                  <col className="max-md:hidden" style={{ width: "18%" }} />
                  <col style={{ width: "10%" }} />
                </colgroup>
                <thead className="bg-ele-inset font-menu text-xs uppercase tracking-widest text-ele-blue">
                  <tr>
                    <th className="px-3 py-2">Engineer</th>
                    <th className="px-3 py-2">Site</th>
                    <th className="px-3 py-2">On site</th>
                    <th className="px-3 py-2 max-md:hidden">How it ended</th>
                    <th className="px-3 py-2 text-right">{isAdmin ? "Delete" : ""}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent.map((s) => (
                    <tr key={s.id} className="border-t border-ele-purple-accent/40">
                      <td className="px-3 py-2 text-ele-strong">{s.engineerName}</td>
                      <td className="px-3 py-2 text-ele-body">
                        {s.siteName} · {s.ref}
                      </td>
                      <td className="px-3 py-2 text-ele-body">
                        {timeOfDay(s.startedAt)} to {s.endedAt ? timeOfDay(s.endedAt) : "?"}
                        {s.alerted && <StatusPill className="ml-2" word="Alerted" tone="amber" />}
                      </td>
                      <td className="px-3 py-2 text-ele-body max-md:hidden">
                        {s.endedReasonLabel}
                        {s.officeNote ? `. ${s.officeNote}` : ""}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {/* RULE 1. Admin only, and the server refuses 409 once a session has raised
                            an alert: at that point the row is the record of what the office was told
                            and how it was answered, which is not ours to erase. The control is shown
                            anyway rather than hidden, so the refusal explains itself. */}
                        {isAdmin && (
                          <button
                            onClick={() => {
                              setRemoveError(null);
                              setConfirmRemove(s);
                            }}
                            className="rounded-ele border border-ele-purple-accent px-2 py-1 text-xs text-ele-body transition hover:border-ele-danger hover:text-ele-danger"
                          >
                            Delete
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          <p className="mt-4 max-w-3xl text-xs text-ele-body">
            A missed check-in tells the office {formatMinutes(data.policy.firstAlertAfterMins)} after it
            falls due, and repeats every {formatMinutes(data.policy.repeatEveryMins)} until the engineer
            checks in or you close it here. The alert goes to your desktop notifications and to the
            office mailbox, with a text to the engineers as the fallback if no desktop notification can
            be delivered.
          </p>
        </>
      )}

      {closeEditor && (
        <ModalShell ariaLabel="Close this check-in" onClose={() => !closing && setCloseEditor(null)} width="max-w-[480px]">
          <h2 className="mb-1 font-heading text-xl tracking-wide text-ele-strong">Close this check-in</h2>
          <p className="text-sm text-ele-body">
            This stops the alerts for {closeEditor.session.engineerName} at {closeEditor.session.siteName}. It
            does not check them in: only they can do that, from their own phone. Close this once you have
            established they are safe.
          </p>
          <label className="mt-3 block">
            <span className={fieldLabelCls}>What happened (optional)</span>
            <input
              className={fieldInputCls}
              value={closeEditor.note}
              onChange={(e) => setCloseEditor((f) => (f ? { ...f, note: e.target.value } : f))}
              placeholder="Rang him, he is fine and on his way home"
              maxLength={500}
            />
          </label>
          {closeError && (
            <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
              {closeError}
            </p>
          )}
          <div className={MODAL_ACTIONS}>
            <button
              type="button"
              onClick={() => setCloseEditor(null)}
              disabled={closing}
              className={`${MODAL_ACTION_BTN} border border-ele-purple-accent text-ele-body disabled:opacity-50`}
            >
              Cancel
            </button>
            <Button onClick={() => void closeSession()} disabled={closing} className={MODAL_ACTION_BTN}>
              {closing ? "Closing..." : "Close the check-in"}
            </Button>
          </div>
        </ModalShell>
      )}

      <ConfirmDeleteModal
        open={confirmRemove !== null}
        title="Delete this check-in record"
        message="The record of this visit's check-in and check-out is removed permanently. A check-in that raised a safety alert cannot be deleted: close it instead."
        confirmLabel="Delete the record"
        busy={removing}
        error={removeError}
        onConfirm={() => void removeSession()}
        onCancel={() => setConfirmRemove(null)}
      />
    </div>
  );
}
